/**
 * Change-freeze windows — Step 143.
 *
 * Month end, a broadcast event, exam season: periods when change is forbidden.
 * A window is a first-class record with an owner and a reason. It does not
 * block a save — the change calendar shades it, the change tab warns inside
 * it, and the "Flag a change planned inside a freeze window" rule (draft,
 * seeded) says what happens. Emergency changes are let through by default.
 *
 * Draft-first: the editor is a form with Save; nothing autosaves. Delete is
 * the one atomic act, behind a confirm.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Select } from '../../components/ui/select';
import { Skeleton } from '../../components/ui/skeleton';
import { Badge } from '../../components/ui/badge';
import { apiFetch, apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useUrlState } from '../../lib/urlState';
import { useDirty } from '../../lib/dirty';
import { useConfirm } from '../../lib/confirm';
import { cn } from '../../lib/utils';

export interface FreezeWindow {
  id: string;
  name: string;
  reason: string | null;
  startsAt: number;
  endsAt: number;
  ownerId: string | null;
  ownerName: string | null;
  allowEmergency: boolean;
}
interface Payload {
  entries: FreezeWindow[];
  people: { id: string; name: string | null; email: string }[];
}

/** Epoch ms ↔ the datetime-local input, in local time. */
const toLocal = (ms: number | null | undefined) => {
  if (!ms) return '';
  const d = new Date(ms);
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
};
const fromLocal = (s: string) => (s ? new Date(s).getTime() : NaN);
const RANGE = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
});

export function FreezeWindowsPage() {
  const [selectedId, setSelectedId] = useUrlState('window', null);
  const loaded = useQuery({
    queryKey: qk.freezeWindows,
    queryFn: () => apiFetch<Payload>('/api/admin/freeze-windows'),
  });
  const now = Date.now();
  return (
    <div className="p-6">
      <div className="mb-1 flex items-center gap-3">
        <CalendarOff className="h-5 w-5 text-muted-foreground" />
        <h1 className="text-lg font-semibold">Freeze windows</h1>
      </div>
      <p className="mb-5 max-w-2xl text-sm text-muted-foreground">
        Periods when change is forbidden — month end, a broadcast event, exam season. The change calendar
        shades them and the change tab warns inside them; the seeded rule "Flag a change planned inside a
        freeze window" adds the note. Emergency changes are let through unless a window says otherwise.
      </p>
      {loaded.isError && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/5 p-4">
          <p className="text-sm font-medium">Freeze windows could not be loaded.</p>
          <Button className="mt-2" size="sm" variant="outline" onClick={() => void loaded.refetch()}>
            Retry
          </Button>
        </div>
      )}
      {loaded.isPending && <Skeleton className="h-48 w-full" />}
      {loaded.data && (
        <div className="grid gap-6 lg:grid-cols-[1fr,26rem]">
          <div>
            <div className="mb-3 flex items-center gap-2">
              <p className="text-sm text-muted-foreground">
                {loaded.data.entries.length} {loaded.data.entries.length === 1 ? 'window' : 'windows'}
              </p>
              <div className="flex-1" />
              <Button size="sm" onClick={() => setSelectedId('new')}>
                <Plus /> New window
              </Button>
            </div>
            <div className="divide-y rounded-lg border bg-card">
              {loaded.data.entries.map((w) => {
                const active = w.startsAt <= now && w.endsAt > now;
                const past = w.endsAt <= now;
                return (
                  <button
                    key={w.id}
                    onClick={() => setSelectedId(w.id)}
                    className={cn(
                      'flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-accent/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                      selectedId === w.id && 'bg-brand/5',
                      past && 'opacity-60',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                        {w.name}
                        {active && (
                          <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">
                            In force now
                          </Badge>
                        )}
                        {past && <Badge variant="secondary">Past</Badge>}
                        {!w.allowEmergency && <Badge variant="outline">Blocks emergency changes too</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {RANGE.format(w.startsAt)} → {RANGE.format(w.endsAt)}
                        {w.ownerName ? ` · owned by ${w.ownerName}` : ''}
                      </p>
                      {w.reason && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">{w.reason}</p>
                      )}
                    </div>
                  </button>
                );
              })}
              {loaded.data.entries.length === 0 && (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">
                  No freeze windows yet. Month end is the usual first one.
                </p>
              )}
            </div>
          </div>
          <div>
            {selectedId ? (
              <WindowEditor
                key={selectedId}
                window={
                  selectedId === 'new' ? null : (loaded.data.entries.find((w) => w.id === selectedId) ?? null)
                }
                people={loaded.data.people}
                onClose={() => setSelectedId(null)}
              />
            ) : (
              <p className="rounded-lg border border-dashed p-8 text-center text-sm text-muted-foreground">
                Pick a window to edit it, or add one.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function WindowEditor({
  window: w,
  people,
  onClose,
}: {
  window: FreezeWindow | null;
  people: Payload['people'];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const confirm = useConfirm();
  const stored = {
    name: w?.name ?? '',
    reason: w?.reason ?? '',
    startsAt: toLocal(w?.startsAt),
    endsAt: toLocal(w?.endsAt),
    ownerId: w?.ownerId ?? '',
    allowEmergency: w?.allowEmergency ?? true,
  };
  const [draft, setDraft] = useState(stored);
  const [error, setError] = useState<string | null>(null);
  const dirty = JSON.stringify(draft) !== JSON.stringify(stored);
  const clearDirty = useDirty(dirty);
  const invalid =
    !draft.name.trim() ||
    !draft.startsAt ||
    !draft.endsAt ||
    !(fromLocal(draft.endsAt) > fromLocal(draft.startsAt));
  const body = () => ({
    name: draft.name.trim(),
    reason: draft.reason.trim() || null,
    startsAt: fromLocal(draft.startsAt),
    endsAt: fromLocal(draft.endsAt),
    ownerId: draft.ownerId || null,
    allowEmergency: draft.allowEmergency,
  });
  const save = useMutation({
    mutationFn: () =>
      w
        ? apiSend(`/api/admin/freeze-windows/${w.id}`, 'PUT', body())
        : apiSend<{ id: string }>('/api/admin/freeze-windows', 'POST', body()),
    onSuccess: (res) => {
      if (!res.ok) return setError(res.error ?? 'That could not be saved.');
      setError(null);
      void qc.invalidateQueries({ queryKey: qk.freezeWindows });
      /**
       * Step 171 — clear the guard BEFORE navigating away.
       *
       * Closing this editor is a URL change, and the router blocker is what
       * intercepts URL changes while anything is dirty. Without this the guard
       * fires on the save that just succeeded: the dialog appears, the panel
       * stays open behind it, and the only offer is to discard work that is
       * already in the database. `useDirty` returns this for exactly this
       * case; the blocker reads the same set synchronously, so clearing here
       * is visible by the time the navigation is evaluated.
       */
      clearDirty();
      toast.success(w ? 'Saved.' : 'Freeze window added.');
      onClose();
    },
  });
  const remove = useMutation({
    mutationFn: () => apiSend(`/api/admin/freeze-windows/${w!.id}`, 'DELETE'),
    onSuccess: (res) => {
      if (!res.ok) return toast.error(res.error ?? 'That did not work.');
      clearDirty();
      void qc.invalidateQueries({ queryKey: qk.freezeWindows });
      onClose();
    },
  });
  return (
    <div className="rounded-lg border bg-card">
      <div className="border-b p-4">
        <p className="font-semibold">{w ? w.name : 'New freeze window'}</p>
      </div>
      <div className="space-y-3 p-4">
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
          <Input
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="e.g. Month-end close"
            autoFocus
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Starts</label>
            <Input
              type="datetime-local"
              value={draft.startsAt}
              onChange={(e) => setDraft({ ...draft, startsAt: e.target.value })}
              className="dark:[color-scheme:dark]"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">Ends</label>
            <Input
              type="datetime-local"
              value={draft.endsAt}
              onChange={(e) => setDraft({ ...draft, endsAt: e.target.value })}
              className="dark:[color-scheme:dark]"
            />
          </div>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Owner — who can say yes to an exception
          </label>
          <Select value={draft.ownerId} onChange={(e) => setDraft({ ...draft, ownerId: e.target.value })}>
            <option value="">—</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name ?? p.email}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">Reason</label>
          <Textarea
            value={draft.reason}
            onChange={(e) => setDraft({ ...draft, reason: e.target.value })}
            placeholder="Why change is frozen — it is shown on the change tab"
            className="min-h-[72px]"
          />
        </div>
        <label className="flex items-start gap-2 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-input"
            checked={draft.allowEmergency}
            onChange={(e) => setDraft({ ...draft, allowEmergency: e.target.checked })}
          />
          <span>
            Emergency changes may still go ahead
            <span className="block text-xs text-muted-foreground">
              Untick for a hard freeze — the warning and the rule then apply to every kind of change.
            </span>
          </span>
        </label>
        {error && <p className="text-xs text-destructive">{error}</p>}
        {draft.startsAt && draft.endsAt && !(fromLocal(draft.endsAt) > fromLocal(draft.startsAt)) && (
          <p className="text-xs text-destructive">The window must end after it starts.</p>
        )}
        <div className="flex items-center justify-end gap-2 border-t pt-3">
          {dirty && <span className="text-xs text-muted-foreground">Unsaved changes</span>}
          <Button
            variant="ghost"
            size="sm"
            disabled={!dirty || save.isPending}
            onClick={() => setDraft(stored)}
          >
            Discard
          </Button>
          <Button size="sm" disabled={!dirty || invalid || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : w ? 'Save' : 'Add window'}
          </Button>
        </div>
      </div>
      <div className="flex items-center gap-2 border-t p-4">
        {w && (
          <Button
            variant="outline"
            size="sm"
            className="text-destructive"
            onClick={async () => {
              if (
                await confirm({
                  title: `Delete “${w.name}”?`,
                  body: 'Changes planned inside it lose their warning. Nothing else is affected.',
                  destructive: true,
                  confirmLabel: 'Delete',
                })
              )
                remove.mutate();
            }}
          >
            <Trash2 /> Delete
          </Button>
        )}
        <div className="flex-1" />
        <button onClick={onClose} className="text-xs text-muted-foreground hover:underline">
          Close
        </button>
      </div>
    </div>
  );
}
